import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, XCircle, EnvelopeSimple, Phone, IdentificationCard, Upload } from '@phosphor-icons/react';
import { uploadApi } from '../utils/api';

export default function VerificationPage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [emailOTP, setEmailOTP] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneOTP, setPhoneOTP] = useState('');
  const [idType, setIdType] = useState('drivers_license');
  const [idFile, setIdFile] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [emailOTPSent, setEmailOTPSent] = useState(false);
  const [phoneOTPSent, setPhoneOTPSent] = useState(false);

  const sendEmailOTP = async () => {
    try {
      setLoading(true);
      const response = await api.post('/api/verification/send-email-otp', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OTP sent to your email');
      setEmailOTPSent(true);
      // Show OTP in dev mode
      if (response.data.otp) {
        toast.info(`Dev Mode - OTP: ${response.data.otp}`);
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailOTP = async () => {
    try {
      setLoading(true);
      await api.post(`/api/verification/verify-email-otp?otp=${emailOTP}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Email verified!');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneOTP = async () => {
    if (!phoneNumber) {
      toast.error('Please enter phone number');
      return;
    }
    try {
      setLoading(true);
      const response = await api.post(`/api/verification/send-phone-otp?phone_number=${phoneNumber}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('OTP sent to your phone');
      setPhoneOTPSent(true);
      if (response.data.otp) {
        toast.info(`Dev Mode - OTP: ${response.data.otp}`);
      }
    } catch (error) {
      toast.error('Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneOTP = async () => {
    try {
      setLoading(true);
      await api.post(`/api/verification/verify-phone-otp?phone_number=${phoneNumber}&otp=${phoneOTP}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Phone verified!');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const uploadID = async () => {
    if (!idFile || !selfieFile) {
      toast.error('Please upload both ID and selfie');
      return;
    }
    
    try {
      setLoading(true);
      
      // Upload ID image
      const idResponse = await uploadApi.upload(idFile, token);
      const selfieResponse = await uploadApi.upload(selfieFile, token);
      
      // Submit for verification
      await api.post('/api/verification/upload-id', {
        id_type: idType,
        id_image_path: idResponse.data.path,
        selfie_image_path: selfieResponse.data.path
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('ID submitted for verification. Admin will review shortly.');
    } catch (error) {
      toast.error('Failed to upload ID');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#050505] py-12">
      <div className="max-w-3xl mx-auto px-6">
        <h1 className="text-3xl font-serif text-white mb-2">Account Verification</h1>
        <p className="text-zinc-400 mb-2">Complete verification to start booking experiences</p>
        <p className="text-zinc-500 text-sm mb-8">
          {user.is_host 
            ? "As a host, you need to complete all 3 verifications (Email, Phone, and ID) to list your spaces."
            : "Guests need Email and Phone verification to book spaces. ID verification is only required for hosts."
          }
        </p>

        <div className="space-y-6">
          {/* Email Verification */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <EnvelopeSimple size={24} className="text-[#D4AF37]" weight="duotone" />
                <h3 className="text-xl font-serif text-white">Email Verification</h3>
              </div>
              {user.email_verified ? (
                <CheckCircle size={24} className="text-green-500" weight="fill" />
              ) : (
                <XCircle size={24} className="text-zinc-600" weight="fill" />
              )}
            </div>
            
            {!user.email_verified && (
              <div className="space-y-3">
                {!emailOTPSent ? (
                  <Button
                    onClick={sendEmailOTP}
                    disabled={loading}
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                  >
                    Send OTP to Email
                  </Button>
                ) : (
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter 6-digit OTP"
                      value={emailOTP}
                      onChange={(e) => setEmailOTP(e.target.value)}
                      className="bg-transparent border-white/20 text-white"
                    />
                    <Button
                      onClick={verifyEmailOTP}
                      disabled={loading || emailOTP.length !== 6}
                      className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                    >
                      Verify
                    </Button>
                  </div>
                )}
              </div>
            )}
            {user.email_verified && (
              <p className="text-green-500">✓ Email verified</p>
            )}
          </div>

          {/* Phone Verification */}
          <div className="bg-[#121212] border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Phone size={24} className="text-[#D4AF37]" weight="duotone" />
                <h3 className="text-xl font-serif text-white">Phone Verification</h3>
              </div>
              {user.phone_verified ? (
                <CheckCircle size={24} className="text-green-500" weight="fill" />
              ) : (
                <XCircle size={24} className="text-zinc-600" weight="fill" />
              )}
            </div>
            
            {!user.phone_verified && (
              <div className="space-y-3">
                <Input
                  placeholder="Enter phone number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="bg-transparent border-white/20 text-white"
                  disabled={phoneOTPSent}
                />
                {!phoneOTPSent ? (
                  <Button
                    onClick={sendPhoneOTP}
                    disabled={loading || !phoneNumber}
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                  >
                    Send OTP
                  </Button>
                ) : (
                  <div className="flex gap-3">
                    <Input
                      placeholder="Enter 6-digit OTP"
                      value={phoneOTP}
                      onChange={(e) => setPhoneOTP(e.target.value)}
                      className="bg-transparent border-white/20 text-white"
                    />
                    <Button
                      onClick={verifyPhoneOTP}
                      disabled={loading || phoneOTP.length !== 6}
                      className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                    >
                      Verify
                    </Button>
                  </div>
                )}
              </div>
            )}
            {user.phone_verified && (
              <p className="text-green-500">✓ Phone verified</p>
            )}
          </div>

          {/* ID Verification - Only shown for hosts or users who want to become hosts */}
          {(user.is_host || user.id_verified) && (
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <IdentificationCard size={24} className="text-[#D4AF37]" weight="duotone" />
                  <h3 className="text-xl font-serif text-white">ID Verification</h3>
                  <span className="text-xs bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-1 rounded">Hosts Only</span>
                </div>
                {user.id_verified ? (
                  <CheckCircle size={24} className="text-green-500" weight="fill" />
                ) : (
                  <XCircle size={24} className="text-zinc-600" weight="fill" />
                )}
              </div>
              
              {!user.id_verified && (
                <div className="space-y-4">
                  <p className="text-zinc-400 text-sm mb-4">
                    ID verification is required to list spaces as a host. We use Stripe Identity for secure document verification.
                  </p>
                  <div>
                    <Label className="text-white mb-2 block">ID Type</Label>
                    <select
                      value={idType}
                      onChange={(e) => setIdType(e.target.value)}
                      className="w-full bg-[#0A0A0A] border border-white/20 text-white py-3 px-4"
                    >
                      <option value="drivers_license">Driver's License</option>
                      <option value="passport">Passport</option>
                      <option value="state_id">State ID</option>
                    </select>
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2 block">Upload ID</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setIdFile(e.target.files[0])}
                      className="w-full text-zinc-400"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white mb-2 block">Upload Selfie</Label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSelfieFile(e.target.files[0])}
                      className="w-full text-zinc-400"
                    />
                  </div>
                  
                  <Button
                    onClick={uploadID}
                    disabled={loading || !idFile || !selfieFile}
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold"
                  >
                    <Upload size={20} className="mr-2" weight="duotone" />
                    Submit for Verification
                  </Button>
                </div>
              )}
              {user.id_verified && (
                <p className="text-green-500">✓ ID verified</p>
              )}
            </div>
          )}

          {/* Info for guests about ID verification */}
          {!user.is_host && !user.id_verified && (
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-4">
                <IdentificationCard size={24} className="text-zinc-500" weight="duotone" />
                <h3 className="text-lg font-serif text-zinc-400">ID Verification</h3>
                <span className="text-xs bg-zinc-800 text-zinc-400 px-2 py-1 rounded">Hosts Only</span>
              </div>
              <p className="text-zinc-500 text-sm">
                ID verification is only required if you want to become a host and list your spaces. 
                As a guest, you only need email and phone verification to book.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}